// API helper for the Catalog page. Mirrors the JSON shape from
// crates/tres-api/src/handlers/catalog.rs `documents`. Read-only: the Catalog
// browses source documents in the selected graph as a nested tree — each
// :Document root with its :contains hierarchy of sections down to any depth.
// Detail rendering reuses /api/v1/entity.

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from './api';
import { useApiSession } from './useApiSession';

export interface CatalogDocNode {
  uri: string;
  label: string;
  /** Depth in the document: 0 = the :Document root, N = :DocumentLN. */
  level: number;
  children?: CatalogDocNode[];
}

export interface CatalogDocumentsResponse {
  documents: CatalogDocNode[];
  /** The graph holds typed data that just isn't document-shaped (probed only
   *  when `documents` is empty) — "wrong surface", not "missing upload". */
  graph_has_other_data: boolean;
}

export function useCatalogDocuments(graph: string | null) {
  const { token, ready } = useApiSession();
  return useQuery<CatalogDocumentsResponse>({
    queryKey: ['catalog', 'documents', graph ?? '', token ? 'auth' : 'noauth'],
    enabled: ready && !!graph,
    staleTime: 60_000,
    queryFn: async () => {
      if (!graph) throw new Error('graph required');
      const url = `/api/v1/catalog/documents?graph=${encodeURIComponent(graph)}`;
      const r = await apiFetch<CatalogDocumentsResponse>(url, token);
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
  });
}

// ---- generic class/instance browse (the Items view) ----
// Restored alongside the document tree: a catalog holding products/models
// rather than documents browses by class here (dev.13 had removed this view,
// leaving non-document catalogs unrenderable).

export interface CatalogClass {
  uri: string;
  label: string;
  count: number;
}

export interface CatalogClassesResponse {
  classes: CatalogClass[];
}

export interface CatalogInstance {
  uri: string;
  label: string;
}

export interface CatalogInstancesResponse {
  items: CatalogInstance[];
  total: number;
  offset: number;
  limit: number;
}

export function useCatalogClasses(graph: string | null) {
  const { token, ready } = useApiSession();
  return useQuery<CatalogClassesResponse>({
    queryKey: ['catalog', 'classes', graph ?? '', token ? 'auth' : 'noauth'],
    enabled: ready && !!graph,
    staleTime: 60_000,
    queryFn: async () => {
      if (!graph) throw new Error('graph required');
      const url = `/api/v1/catalog/classes?graph=${encodeURIComponent(graph)}`;
      const r = await apiFetch<CatalogClassesResponse>(url, token);
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
  });
}

export function useCatalogInstances(
  graph: string | null,
  classUri: string | null,
  q: string,
  offset: number,
) {
  const { token, ready } = useApiSession();
  return useQuery<CatalogInstancesResponse>({
    queryKey: [
      'catalog',
      'instances',
      graph ?? '',
      classUri ?? '',
      q,
      offset,
      token ? 'auth' : 'noauth',
    ],
    enabled: ready && !!graph && !!classUri,
    staleTime: 60_000,
    queryFn: async () => {
      if (!graph || !classUri) throw new Error('not ready');
      const params = new URLSearchParams({ graph, class: classUri, offset: String(offset) });
      if (q.trim().length > 0) params.set('q', q.trim());
      const url = `/api/v1/catalog/instances?${params.toString()}`;
      const r = await apiFetch<CatalogInstancesResponse>(url, token);
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
  });
}

// ---- the Sources view: every load the catalog records ----
// Mirrors handlers/source_catalog.rs `list` (design/SOURCE_CATALOG_DESIGN.md).

export interface CatalogLoad {
  record: string;
  /** The name the load's statements carry as their source. */
  source: string;
  kind: string | null;
  graph: string | null;
  sheet: string | null;
  imported_at: string | null;
  loaded_by: string | null;
  rows: number | null;
  statements: number | null;
  content_hash: string | null;
  shape_hash: string | null;
  mapping_id: string | null;
  mapping_name: string | null;
  mapping_hash: string | null;
  removed_at: string | null;
}

export interface CatalogSourceGroup {
  name: string;
  shape_hash: string | null;
  /** The saved mapping the sheet is read with now, found by its header set. */
  mapping: { id: string; name: string; updated_at: string; hash: string } | null;
  loads: CatalogLoad[];
}

export function useCatalogSources(enabled: boolean) {
  const { token, ready } = useApiSession();
  return useQuery<{ groups: CatalogSourceGroup[] }>({
    queryKey: ['catalog', 'sources', token ? 'auth' : 'noauth'],
    enabled: ready && enabled,
    staleTime: 30_000,
    queryFn: async () => {
      const r = await apiFetch<{ groups: CatalogSourceGroup[] }>('/api/v1/catalog/sources', token);
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
  });
}
